import { memo, type ReactNode } from 'react';

import { dimensionLabel } from '../../../utils/dimensions';

import styles from './index.module.css';

export interface DimensionRowProps {
  dimensionKey: string;
  justification: string;
  /** 1-5. Omitted for a dimension the product records but does not score. */
  score?: number;
  /** DimensionScoreHistory, when the interview moved this dimension from
   * its resume-derived value. Optional and additive -- every existing call
   * site renders exactly as before. */
  historyBadge?: ReactNode;
}

const SCALE_MAX = 5;

/**
 * One dimension, in the reference mockup's three-column treatment.
 *
 * A row rather than a card, because eight of these are read together and the
 * question a hiring team asks is comparative — where is this person strong,
 * where are they thin. Cards separate; a grid lines the scales up under each
 * other so the shape of a candidate is legible in one pass.
 *
 * The dots are discrete for the same reason. A 4 and a 5 differ by one filled
 * dot, which is countable; as bar widths they differ by 20% of a length, which
 * is not, and the old rendering asked people to compare eight of them.
 */
export const DimensionRow = memo(
  ({ dimensionKey, justification, score, historyBadge }: DimensionRowProps) => {
    const scored = typeof score === 'number' && Number.isFinite(score);

    return (
      <div className={`${styles.row} ${scored ? '' : styles.rowUnscored}`}>
        <div className={styles.name}>
          {dimensionLabel(dimensionKey)}
          {historyBadge}
        </div>

        {/*
          No scale at all for an unscored dimension, and the column collapses
          with it. Rendering an empty or zeroed scale would read as "scored
          zero" or "score missing" — both untrue. It has no score to show.
        */}
        {scored ? (
          <div
            className={styles.scale}
            role="img"
            aria-label={`${score} out of ${SCALE_MAX}`}
          >
            {Array.from({ length: SCALE_MAX }, (_, i) => (
              <span
                key={i}
                aria-hidden="true"
                className={`${styles.dot} ${i < score ? styles.dotFilled : ''}`}
              />
            ))}
            <span className={styles.outOf} aria-hidden="true">
              {score}/{SCALE_MAX}
            </span>
          </div>
        ) : null}

        <p className={styles.justification}>{justification}</p>
      </div>
    );
  },
);

DimensionRow.displayName = 'DimensionRow';

export default DimensionRow;
